import React from 'react';

// =============================================
// INVESTMENTS SECTION COMPONENT
// =============================================
export function InvestmentsSection({
    activeModule,
    investments, setInvestments,
    investmentTotals,
    fmtBase, toBase,
    invCashSGD, setInvCashSGD,
    invCashUSD, setInvCashUSD,
    moneyMarketFunds, setMoneyMarketFunds,
}) {
    return (
        <div className="section-container" style={{ paddingBottom: '100px' }}>

            {/* Portfolio Tab */}
            {activeModule === 'inv-portfolio' && (
                <>
                    <div className="section-header">
                        <div>
                            <div className="section-title">📈 Portfolio</div>
                            <div className="section-subtitle">{investments.length} positions · Moomoo</div>
                        </div>
                        <button className="add-btn" onClick={() => {
                            const ticker = prompt('Ticker symbol (e.g. AAPL):');
                            if (!ticker) return;
                            const name = prompt('Company/ETF name:') || ticker;
                            const units = parseFloat(prompt('Units held:') || 0);
                            const avgCost = parseFloat(prompt('Avg cost per unit (USD):') || 0);
                            const curPrice = parseFloat(prompt('Current price (USD):') || avgCost);
                            const type = prompt('Type (stock/etf):') || 'stock';
                            setInvestments(prev => [...prev, { id: Date.now(), ticker: ticker.toUpperCase(), name, units, avgCostUsd: avgCost, currentPriceUsd: curPrice, currency: 'USD', type }]);
                        }}>+ Add</button>
                    </div>

                    {/* Portfolio summary card */}
                    <div className="summary-card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.05) 100%)', border: '1px solid rgba(99,102,241,0.25)' }}>
                        <div className="summary-card-title" style={{ color: '#818cf8' }}>Portfolio Summary</div>
                        <div className="summary-stats">
                            <div className="summary-stat">
                                <div className="summary-stat-value" style={{ color: '#818cf8' }}>{fmtBase(investmentTotals.securitiesBase, { compact: true })}</div>
                                <div className="summary-stat-label">Market Value</div>
                            </div>
                            <div className="summary-stat">
                                <div className="summary-stat-value" style={{ color: investmentTotals.pnl >= 0 ? '#10B981' : '#EF4444' }}>
                                    {investmentTotals.pnl >= 0 ? '+' : ''}{fmtBase(investmentTotals.pnl, { compact: true })}
                                </div>
                                <div className="summary-stat-label">Unrealised P&L</div>
                            </div>
                            <div className="summary-stat">
                                <div className="summary-stat-value" style={{ color: investmentTotals.pnl >= 0 ? '#10B981' : '#EF4444' }}>
                                    {investmentTotals.pnlPct >= 0 ? '+' : ''}{investmentTotals.pnlPct.toFixed(1)}%
                                </div>
                                <div className="summary-stat-label">Return %</div>
                            </div>
                        </div>
                    </div>

                    {investments.length === 0 ? (
                        <div className="empty-state"><div className="empty-icon">📈</div><div className="empty-text">No positions yet — tap Add to add a holding</div></div>
                    ) : investments.map((inv, idx) => {
                        const mktValue = inv.units * (inv.currentPriceUsd || 0);
                        const cost = inv.units * (inv.avgCostUsd || 0);
                        const pnl = mktValue - cost;
                        const pnlPct = cost > 0 ? (pnl / cost) * 100 : 0;
                        const mktValueBase = toBase(mktValue, 'USD');
                        return (
                            <div key={inv.id} className="item-card">
                                <div className="card-header">
                                    <div>
                                        <div className="card-title">{inv.ticker}</div>
                                        <div className="card-subtitle">{inv.name} · {inv.type?.toUpperCase()}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#818cf8' }}>{fmtBase(mktValueBase)}</div>
                                        <div style={{ fontSize: '12px', color: pnl >= 0 ? '#10B981' : '#EF4444', fontWeight: '600' }}>
                                            {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%)
                                        </div>
                                    </div>
                                </div>
                                <div className="card-details">
                                    <div className="detail-item">
                                        <div className="detail-label">Units</div>
                                        <div className="detail-value">{inv.units}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Avg Cost</div>
                                        <div className="detail-value">${(inv.avgCostUsd || 0).toFixed(2)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Current Price</div>
                                        <div className="detail-value">
                                            <input
                                                type="number"
                                                value={inv.currentPriceUsd || ''}
                                                onChange={e => setInvestments(prev => prev.map((x, i) => i === idx ? { ...x, currentPriceUsd: parseFloat(e.target.value) || 0 } : x))}
                                                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '4px 8px', color: 'var(--primary)', fontWeight: '700', fontSize: '14px', width: '100px', fontFamily: 'inherit' }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Cost Basis</div>
                                        <div className="detail-value">${cost.toFixed(2)}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                    <button onClick={() => {
                                        const newUnits = parseFloat(prompt(`Update units for ${inv.ticker} (current: ${inv.units}):`) || inv.units);
                                        const newAvg = parseFloat(prompt(`Update avg cost USD (current: ${inv.avgCostUsd}):`) || inv.avgCostUsd);
                                        setInvestments(prev => prev.map((x, i) => i === idx ? { ...x, units: newUnits, avgCostUsd: newAvg } : x));
                                    }} style={{ flex: 1, padding: '8px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px', color: '#a5b4fc', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                        ✏️ Edit
                                    </button>
                                    <button onClick={() => { if (confirm(`Remove ${inv.ticker}?`)) setInvestments(prev => prev.filter((_, i) => i !== idx)); }}
                                        style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: '#fca5a5', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}

            {/* Cash Tab */}
            {activeModule === 'inv-cash' && (
                <>
                    <div className="section-header">
                        <div>
                            <div className="section-title">💵 Cash Wallets</div>
                            <div className="section-subtitle">Moomoo cash positions</div>
                        </div>
                    </div>
                    <div className="summary-card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(16,185,129,0.04) 100%)', border: '1px solid rgba(16,185,129,0.25)' }}>
                        <div className="summary-card-title" style={{ color: '#6ee7b7' }}>Total Cash</div>
                        <div style={{ fontSize: '28px', fontWeight: '700', color: '#6ee7b7', marginTop: '8px' }}>
                            {fmtBase(toBase(invCashSGD, 'SGD') + toBase(invCashUSD, 'USD'))}
                        </div>
                    </div>
                    {[
                        { label: 'SGD Cash Wallet', value: invCashSGD, setter: setInvCashSGD, symbol: 'S$', currency: 'SGD' },
                        { label: 'USD Cash Wallet', value: invCashUSD, setter: setInvCashUSD, symbol: 'US$', currency: 'USD' },
                    ].map(({ label, value, setter, symbol, currency }) => (
                        <div key={currency} className="item-card">
                            <div className="card-header">
                                <div>
                                    <div className="card-title">{label}</div>
                                    <div className="card-subtitle">Moomoo · {currency}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#6ee7b7' }}>{fmtBase(toBase(value, currency))}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{symbol}{(value || 0).toLocaleString()}</div>
                                </div>
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">Balance ({symbol})</label>
                                <input type="number" className="form-input" value={value || ''} step="0.01"
                                    onChange={e => setter(parseFloat(e.target.value) || 0)} placeholder="0.00" />
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* Money Market Tab */}
            {activeModule === 'inv-mmf' && (
                <>
                    <div className="section-header">
                        <div>
                            <div className="section-title">🏦 Money Market Funds</div>
                            <div className="section-subtitle">{moneyMarketFunds.length} funds</div>
                        </div>
                        <button className="add-btn" onClick={() => {
                            const name = prompt('Fund name:');
                            if (!name) return;
                            const currency = prompt('Currency (SGD/USD):') || 'SGD';
                            setMoneyMarketFunds(prev => [...prev, { id: Date.now(), name, currency, units: 0, nav: 1.00, totalValue: 0 }]);
                        }}>+ Add</button>
                    </div>
                    <div className="summary-card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(245,158,11,0.04) 100%)', border: '1px solid rgba(245,158,11,0.25)' }}>
                        <div className="summary-card-title" style={{ color: '#fcd34d' }}>Total MMF Value</div>
                        <div style={{ fontSize: '28px', fontWeight: '700', color: '#fcd34d', marginTop: '8px' }}>
                            {fmtBase(investmentTotals.mmfBase)}
                        </div>
                    </div>
                    {moneyMarketFunds.length === 0 ? (
                        <div className="empty-state"><div className="empty-icon">🏦</div><div className="empty-text">No funds yet</div></div>
                    ) : moneyMarketFunds.map((fund, idx) => (
                        <div key={fund.id} className="item-card">
                            <div className="card-header">
                                <div>
                                    <div className="card-title">{fund.name}</div>
                                    <div className="card-subtitle">Money Market · {fund.currency}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#fcd34d' }}>{fmtBase(toBase(fund.totalValue || 0, fund.currency))}</div>
                                </div>
                            </div>
                            <div className="card-details">
                                <div className="detail-item">
                                    <div className="detail-label">Units</div>
                                    <div className="detail-value">
                                        <input type="number" value={fund.units || ''} step="0.0001"
                                            onChange={e => {
                                                const units = parseFloat(e.target.value) || 0;
                                                setMoneyMarketFunds(prev => prev.map((f, i) => i === idx ? { ...f, units, totalValue: units * (f.nav || 1) } : f));
                                            }}
                                            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '4px 8px', color: 'var(--text-primary)', fontSize: '14px', width: '110px', fontFamily: 'inherit' }} />
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <div className="detail-label">NAV</div>
                                    <div className="detail-value">
                                        <input type="number" value={fund.nav || ''} step="0.0001"
                                            onChange={e => {
                                                const nav = parseFloat(e.target.value) || 1;
                                                setMoneyMarketFunds(prev => prev.map((f, i) => i === idx ? { ...f, nav, totalValue: (f.units || 0) * nav } : f));
                                            }}
                                            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '4px 8px', color: 'var(--text-primary)', fontSize: '14px', width: '90px', fontFamily: 'inherit' }} />
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <div className="detail-label">Total Value</div>
                                    <div className="detail-value highlight">{fund.currency === 'USD' ? '$' : 'S$'}{(fund.totalValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                            </div>
                            <button onClick={() => { if (confirm(`Remove ${fund.name}?`)) setMoneyMarketFunds(prev => prev.filter((_, i) => i !== idx)); }}
                                style={{ width: '100%', marginTop: '8px', padding: '8px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: '#fca5a5', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                🗑️ Remove Fund
                            </button>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}
